import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatSelect } from '@angular/material/select';
import { MatSelectChange } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../confirm-dialog/confirm-dialog.component';
import { SelectStopDialogComponent } from './select-stop-dialog.component';
import { debounceTime } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from '../../auth.service';


interface Lading {
  alternate_reference: string;
  shipper_name: string;
  shipper_contact_uuid: string;
  shipper_locality: string;
  shipper_region: string;
  consignee_name: string;
  consignee_contact_uuid: string;
  consignee_locality: string;
  consignee_region: string;
  load_pieces: number;
  load_weight: number;
  load_pallets: number;
  load_length: number;
  lading_uuid: string;
}

interface Terminal {
  terminal_name: string;
  contact_uuid: string;
  address_street: string;
  address_locality: string;
  address_region: string;
  latitude: string;
  longitude: string;
}

interface Carrier {
  carrier_contact_uuid: string;
  carrier_name: string;
  usage_count: number; // maybe laters
}

export interface Stop {
  stopNumber: number;
  shipper: string;
  shipperContact: {
    locality: string;
    region: string;
    contact_uuid: string;
  };
  consignee: string;
  consigneeContact: {
    locality: string;
    region: string;
    contact_uuid: string;
  };
  ladings: Lading[];
  ladingsDataSource: MatTableDataSource<Lading>;
  totalLoadPieces: number;
  totalLoadWeight: number;
  totalLoadPallets: number;
  totalLoadLength: number;
  selectedConsignee: Terminal | null;
}

@Component({
  selector: 'app-consolidator',
  templateUrl: './consolidator.component.html',
  styleUrls: ['./consolidator.component.scss'],
})
export class ConsolidatorComponent implements OnInit {
  isSaving = false;
  recoveryReady = false;
  pendingConsolidationRequest: any = null;
  consolidationMessage = '';
  private pendingStorageKey: string | null = null;

  ladings: Lading[] = [];
  filteredLadings: Lading[] = [];
  displayedColumns: string[] = [
    'order_no',
    'shipper_name',
    'shipper_location',
    'consignee_name',
    'consignee_location',
    'load_weight',
    'load_pieces',
    'load_pallets',
    'load_length',
    'actions',
  ];
  stopColumns: string[] = [
    'expand',
    'stopNumber',
    'shipper',
    'shipper_location',
    'consignee',
    'consignee_location',
    'totalLoadPieces',
    'totalLoadWeight',
    'totalLoadPallets',
    'totalLoadLength',
    'numLadings',
    'actions',
  ];
  selectedLadings: Lading[] = [];
  consolidationStops: Stop[] = [];
  stopsDataSource = new MatTableDataSource<Stop>([]);
  searchQuery: string = '';

  masterLading: any = {
    shipper: '',
    consignee: '',
    totalLoadPieces: 0,
    totalLoadWeight: 0,
    totalLoadPallets: 0,
    totalLoadLength: 0,
  };

  apiUrl: string = './api/work_orders/lading_consolidation_prospects.php';
  terminalsUrl: string = './api/work_orders/get_terminals.php';

  masterShipper: any = {
    name: '',
    contact_uuid: '',
    locality: '',
    region: '',
  };
  
  masterCarrier: any = {
    name: '',
    contact_uuid: '',
  };

  selectedShipper: Terminal | null = null;

  terminals: Terminal[] = [];

  expandedElement: any | null;

  carriers: Carrier[] = [];
  filteredCarriers: Carrier[] = [];
  selectedCarrier: Carrier | null = null;
  carrierFilter: string = '';

  @ViewChild('carrierSelect') carrierSelect!: MatSelect;

  constructor(private http: HttpClient, public dialog: MatDialog, private router: Router, private toastr: ToastrService, private auth: AuthService) {}

  ngOnInit() {
    this.auth.ensureCurrentUserLoaded().subscribe({
      next: user => {
        if (!user) { this.consolidationMessage = 'Sign in before saving a consolidation.'; return; }
        this.pendingStorageKey = 'roswell.consolidation.pending.v1:' + user.user_id;
        try {
          const saved = sessionStorage.getItem(this.pendingStorageKey);
          if (saved) {
            const request = JSON.parse(saved);
            if (!Array.isArray(request?.stops) || !request.stops.length) throw new Error('Invalid saved request');
            this.pendingConsolidationRequest = request;
            this.consolidationMessage = 'An unconfirmed save was restored. Check its status before starting another consolidation.';
          }
          this.recoveryReady = true;
        } catch {
          this.consolidationMessage = 'The saved request could not be restored. Check Roswell status before starting another consolidation.';
        }
      },
      error: () => { this.consolidationMessage = 'Sign-in could not be checked. Refresh before saving.'; }
    });
    this.fetchLadings();
    this.fetchTerminals();
    this.fetchCarriers();

  // Close the dropdown when a carrier is selected
  if (this.carrierSelect) {
    this.carrierSelect.selectionChange.subscribe(() => {
      this.carrierSelect.close();
    });
  }
  }

  fetchLadings() {
    this.http.get<any>(this.apiUrl).subscribe(
      (response) => {
        if (response && Array.isArray(response.data)) {
          this.ladings = response.data;
          this.filteredLadings = [...this.ladings];
        } else {
          console.error('Unexpected ladings response format:', response);
        }
      },
      (error) => {
        console.error('Error fetching ladings:', error);
      }
    );
  }

  fetchTerminals() {
    console.log('Fetching terminals from URL:', this.terminalsUrl);
    this.http.get<Terminal[]>(this.terminalsUrl).subscribe(
      (response) => {
        console.log('Terminals response:', response);
        if (Array.isArray(response)) {
          this.terminals = response;
        } else {
          console.error('Unexpected terminals response format:', response);
        }
      },
      (error) => {
        console.error('Error fetching terminals:', error);
      }
    );
  }

  handleShipperChange(event: MatSelectChange) {
    const selectedTerminal: Terminal = event.value;

    if (selectedTerminal) {
      this.masterShipper = {
        name: selectedTerminal.terminal_name,
        contact_uuid: selectedTerminal.contact_uuid,
        locality: selectedTerminal.address_locality,
        region: selectedTerminal.address_region,
      };

      // Update shipper info in all stops
      this.consolidationStops.forEach((stop) => {
        stop.shipper = this.masterShipper.name;
        stop.shipperContact = {
          locality: this.masterShipper.locality,
          region: this.masterShipper.region,
          contact_uuid: this.masterShipper.contact_uuid,
        };
      });

      this.filterLadings();
      this.updateMasterLading();
      this.updateStopNumbers();
    }
  }

  handleConsigneeChange(event: MatSelectChange, stop: Stop): void {
    const selectedConsignee: Terminal = event.value;
  
    if (selectedConsignee) {
      // Assign selected consignee to the stop
      stop.consignee = selectedConsignee.terminal_name;
      stop.consigneeContact = {
        locality: selectedConsignee.address_locality,
        region: selectedConsignee.address_region,
        contact_uuid: selectedConsignee.contact_uuid,
      };
      stop.selectedConsignee = selectedConsignee;
  
      // Check if another stop with the same consignee exists
      const existingStop = this.consolidationStops.find(
        (s) =>
          s.consigneeContact.contact_uuid === selectedConsignee.contact_uuid &&
          s !== stop // Exclude the current stop
      );
  
      if (existingStop) {
        // Prompt the user if they want to regroup
        setTimeout(() => {
          const dialogData: ConfirmDialogData = {
            title: 'Regroup Stops',
            message: `A stop with consignee "${existingStop.consignee}" already exists. Do you want to regroup the ladings into this stop?`,
            confirmText: 'Yes',
            cancelText: 'No',
          };
  
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: dialogData,
            autoFocus: true,
            restoreFocus: false,
          });
  
          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              // User chose to regroup
              // Move ladings from current stop to existing stop
              existingStop.ladings.push(...stop.ladings);
              existingStop.ladingsDataSource.data = [...existingStop.ladings]; // Update data source
  
              // Update totals
              existingStop.totalLoadPieces += stop.totalLoadPieces;
              existingStop.totalLoadWeight += stop.totalLoadWeight;
              existingStop.totalLoadPallets += stop.totalLoadPallets;
              existingStop.totalLoadLength += stop.totalLoadLength;
  
              // Remove the current stop
              this.consolidationStops = this.consolidationStops.filter((s) => s !== stop);
              this.stopsDataSource.data = [...this.consolidationStops]; // Update data source
  
              this.updateMasterLading();
              this.updateStopNumbers();
            } else {
              // User chose not to regroup; keep the stop as is
              // No further action needed
            }
          });
        }, 0);
      }
    }
  }

  filterLadings() {
    const query = this.searchQuery ? this.searchQuery.toLowerCase() : '';
    const queries = query.split('|').map(q => q.trim()).filter(q => q.length > 0);
  
    this.filteredLadings = this.ladings.filter((lading) => {
      // Exclude ladings already in consolidation
      const isLadingSelected = this.selectedLadings.some(
        (selected) => selected.lading_uuid === lading.lading_uuid
      );
      if (isLadingSelected) {
        return false;
      }
  
      const orderNo = (lading.alternate_reference || '').toLowerCase();
      const consigneeName = (lading.consignee_name || '').toLowerCase();
      const consigneeLocality = (lading.consignee_locality || '').toLowerCase();
      const consigneeRegion = (lading.consignee_region || '').toLowerCase();
  
      // If no queries, just return all that aren't selected
      if (queries.length === 0) {
        return true;
      }
  
      // Return true if any of the queries matches any of the fields
      return queries.some(q =>
        orderNo.includes(q) ||
        consigneeName.includes(q) ||
        consigneeLocality.includes(q) ||
        consigneeRegion.includes(q)
      );
    });
  }

  addToConsolidation(lading: Lading): void {
  console.log('Adding lading:', lading);

  // Remove the lading from filteredLadings and add to selectedLadings
  this.filteredLadings = this.filteredLadings.filter((item) => item !== lading);
  this.selectedLadings.push(lading);

  const loadPieces = Number(lading.load_pieces) || 0;
  const loadWeight = Number(lading.load_weight) || 0;
  const loadPallets = Number(lading.load_pallets) || 0;
  const loadLength = Number(lading.load_length) || 0;


  // Set master shipper if not already set
  if (!this.masterShipper.name) {
    this.masterShipper = {
      name: lading.shipper_name,
      contact_uuid: lading.shipper_contact_uuid,
      locality: lading.shipper_locality,
      region: lading.shipper_region,
    };
    this.selectedShipper = this.terminals.find(
      (terminal) => terminal.contact_uuid === this.masterShipper.contact_uuid
    ) || null;
    this.filterLadings();
  }

  // Create a new stop for the lading
  this.createNewStop(lading, loadPieces, loadWeight, loadPallets, loadLength);

  this.stopsDataSource.data = [...this.consolidationStops];
  this.updateMasterLading();
  this.updateStopNumbers();
}



  addToConsolidationAsOwnStop(lading: Lading): void {
    console.log('Adding lading as its own stop:', lading);
  
    // Remove the lading from filteredLadings and add to selectedLadings
    this.filteredLadings = this.filteredLadings.filter((item) => item !== lading);
    this.selectedLadings.push(lading);
  
    const loadPieces = Number(lading.load_pieces) || 0;
    const loadWeight = Number(lading.load_weight) || 0;
    const loadPallets = Number(lading.load_pallets) || 0;
    const loadLength = Number(lading.load_length) || 0;

  
    // Set master shipper if not already set
    if (!this.masterShipper.name) {
      this.masterShipper = {
        name: lading.shipper_name,
        contact_uuid: lading.shipper_contact_uuid,
        locality: lading.shipper_locality,
        region: lading.shipper_region,
      };
      this.selectedShipper = this.terminals.find(
        (terminal) => terminal.contact_uuid === this.masterShipper.contact_uuid
      ) || null;
      this.filterLadings();
    }
  
    // Find or create the consignee terminal
    let consigneeTerminal = this.terminals.find(
      (terminal) => terminal.contact_uuid === lading.consignee_contact_uuid
    );
  
    if (!consigneeTerminal) {
      // If not found in terminals, create a new Terminal object from lading data
      consigneeTerminal = {
        terminal_name: lading.consignee_name,
        contact_uuid: lading.consignee_contact_uuid,
        address_street: '', // Fill in if available
        address_locality: lading.consignee_locality || '',
        address_region: lading.consignee_region || '',
        latitude: '', // Fill in if available
        longitude: '', // Fill in if available
      };
  
      // Add the new terminal to the terminals list
      this.terminals.push(consigneeTerminal);
    }
  
    // Create a new stop with selectedConsignee set to the consignee terminal
    this.createNewStop(lading, loadPieces, loadWeight, loadPallets, loadLength, consigneeTerminal);
  
    this.stopsDataSource.data = [...this.consolidationStops];
    this.updateMasterLading();
    this.updateStopNumbers();
  }

  makeLadingItsOwnStop(lading: Lading, currentStop: Stop): void {
    // Remove the lading from the current stop
    const loadPieces = Number(lading.load_pieces) || 0;
    const loadWeight = Number(lading.load_weight) || 0;
    const loadPallets = Number(lading.load_pallets) || 0;
    const loadLength = Number(lading.load_length) || 0;

  
    currentStop.ladings = currentStop.ladings.filter((item: Lading) => item !== lading);
    currentStop.ladingsDataSource.data = [...currentStop.ladings]; // Update data source
  
    currentStop.totalLoadPieces -= loadPieces;
    currentStop.totalLoadWeight -= loadWeight;
    currentStop.totalLoadPallets -= loadPallets;
  
    if (currentStop.ladings.length === 0) {
      this.consolidationStops = this.consolidationStops.filter((s) => s !== currentStop);
    }
  
    // Find or create the consignee terminal
    let consigneeTerminal = this.terminals.find(
      (terminal) => terminal.contact_uuid === lading.consignee_contact_uuid
    );
  
    if (!consigneeTerminal) {
      // If not found in terminals, create a new Terminal object from lading data
      consigneeTerminal = {
        terminal_name: lading.consignee_name,
        contact_uuid: lading.consignee_contact_uuid,
        address_street: '', // Fill in if available
        address_locality: lading.consignee_locality || '',
        address_region: lading.consignee_region || '',
        latitude: '', // Fill in if available
        longitude: '', // Fill in if available
      };
  
      // Add the new terminal to the terminals list
      this.terminals.push(consigneeTerminal);
    }
  
    // Create a new stop for the lading using its original consignee
    this.createNewStop(lading, loadPieces, loadWeight, loadPallets, loadLength, consigneeTerminal);
  
    this.updateStopNumbers();
    this.updateMasterLading();
    this.stopsDataSource.data = [...this.consolidationStops];
  }
  
  moveLadingToAnotherStop(lading: Lading, currentStop: Stop): void {
    // Exclude the current stop from the list of available stops
    const availableStops = this.consolidationStops.filter(stop => stop !== currentStop);
  
    if (availableStops.length === 0) {
      // No other stops to move to; maybe notify the user
      console.warn('No other stops available to move the lading to.');
      return;
    }
  
    // Open a dialog for the user to select a stop
    const dialogRef = this.dialog.open(SelectStopDialogComponent, {
      width: '300px',
      data: { stops: availableStops }
    });
  
    dialogRef.afterClosed().subscribe((selectedStop: Stop) => {
      if (selectedStop) {
        const loadPieces = Number(lading.load_pieces) || 0;
        const loadWeight = Number(lading.load_weight) || 0;
        const loadPallets = Number(lading.load_pallets) || 0;
        const loadLength = Number(lading.load_length) || 0;
  
        // Remove the lading from the current stop
        currentStop.ladings = currentStop.ladings.filter(item => item !== lading);
        currentStop.ladingsDataSource.data = [...currentStop.ladings]; // Update data source
  
        // Update totals for current stop
        currentStop.totalLoadPieces -= loadPieces;
        currentStop.totalLoadWeight -= loadWeight;
        currentStop.totalLoadPallets -= loadPallets;
        currentStop.totalLoadLength -= loadLength;
  
        // If current stop has no more ladings, remove it
        if (currentStop.ladings.length === 0) {
          this.consolidationStops = this.consolidationStops.filter(stop => stop !== currentStop);
          this.stopsDataSource.data = [...this.consolidationStops]; // Update data source
        }
  
        // Add the lading to the selected stop
        selectedStop.ladings.push(lading);
        selectedStop.ladingsDataSource.data = [...selectedStop.ladings]; // Update data source
  
        // Update totals for selected stop
        selectedStop.totalLoadPieces += loadPieces;
        selectedStop.totalLoadWeight += loadWeight;
        selectedStop.totalLoadPallets += loadPallets;
  
        // Update the stops data source to reflect any changes
        this.stopsDataSource.data = [...this.consolidationStops];
  
        // Update the master lading and stop numbers
        this.updateMasterLading();
        this.updateStopNumbers();
      }
    });
  }



  private findMatchingStop(lading: Lading): Stop | null {
    return this.consolidationStops.find((stop) => {
      const stopLocality = stop.consigneeContact.locality.toLowerCase();
      const stopRegion = stop.consigneeContact.region.toLowerCase();
      const ladingLocality = lading.consignee_locality.toLowerCase();
      const ladingRegion = lading.consignee_region.toLowerCase();

      return stopLocality === ladingLocality && stopRegion === ladingRegion;
    }) || null;
  }

  private createNewStop(
    lading: Lading,
    loadPieces: number,
    loadWeight: number,
    loadPallets: number,
    loadLength: number,
    selectedConsignee: Terminal | null = null
  ): void {
    const stopConsignee = selectedConsignee ? selectedConsignee.terminal_name : lading.consignee_name;
    const stopConsigneeContact = selectedConsignee
      ? {
          locality: selectedConsignee.address_locality,
          region: selectedConsignee.address_region,
          contact_uuid: selectedConsignee.contact_uuid,
        }
      : {
          locality: lading.consignee_locality || '',
          region: lading.consignee_region || '',
          contact_uuid: lading.consignee_contact_uuid,
        };
  
    const newStop: Stop = {
      stopNumber: this.consolidationStops.length + 1,
      shipper: this.masterShipper.name,
      shipperContact: {
        locality: this.masterShipper.locality,
        region: this.masterShipper.region,
        contact_uuid: this.masterShipper.contact_uuid,
      },
      consignee: stopConsignee,
      consigneeContact: stopConsigneeContact,
      ladings: [lading],
      ladingsDataSource: new MatTableDataSource<Lading>([lading]), 
      totalLoadPieces: loadPieces,
      totalLoadWeight: loadWeight,
      totalLoadPallets: loadPallets,
      totalLoadLength: loadLength,
      selectedConsignee: selectedConsignee,
    };
  
    console.log('Creating new stop:', newStop);
  
    this.consolidationStops.push(newStop);
  }

  removeFromConsolidation(lading: Lading, stop: Stop): void {
    const loadPieces = Number(lading.load_pieces) || 0;
    const loadWeight = Number(lading.load_weight) || 0;
    const loadPallets = Number(lading.load_pallets) || 0;
    const loadLength = Number(lading.load_length) || 0;

  
    // Remove lading from stop
    stop.ladings = stop.ladings.filter(item => item !== lading);
    stop.ladingsDataSource.data = [...stop.ladings]; // Update data source
  
    // Update totals for the stop
    stop.totalLoadPieces -= loadPieces;
    stop.totalLoadWeight -= loadWeight;
    stop.totalLoadPallets -= loadPallets;
    stop.totalLoadLength -= loadLength;

    // If the stop has no more ladings, remove it
    if (stop.ladings.length === 0) {
      this.consolidationStops = this.consolidationStops.filter(s => s !== stop);
      this.stopsDataSource.data = [...this.consolidationStops]; // Update data source
    }
  
    // Remove the lading from selectedLadings and add back to filteredLadings
    this.selectedLadings = this.selectedLadings.filter(item => item !== lading);
    this.filteredLadings.push(lading);
  
    // Update the master lading and stop numbers
    this.updateMasterLading();
    this.updateStopNumbers();
  }

  updateMasterLading() {
    if (this.consolidationStops.length > 0) {
      this.masterLading.shipper = this.masterShipper.name;
      this.masterLading.shipperContact = {
        locality: this.masterShipper.locality,
        region: this.masterShipper.region,
        contact_uuid: this.masterShipper.contact_uuid,
      };

      const lastStop = this.consolidationStops[this.consolidationStops.length - 1];
      this.masterLading.consignee = lastStop.selectedConsignee
        ? lastStop.selectedConsignee.terminal_name
        : lastStop.consignee;
      this.masterLading.consigneeContact = lastStop.selectedConsignee
        ? {
            locality: lastStop.selectedConsignee.address_locality,
            region: lastStop.selectedConsignee.address_region,
            contact_uuid: lastStop.selectedConsignee.contact_uuid,
          }
        : lastStop.consigneeContact;

      this.masterLading.totalLoadPieces = this.consolidationStops.reduce(
        (total, stop) => total + stop.totalLoadPieces,
        0
      );
      this.masterLading.totalLoadWeight = this.consolidationStops.reduce(
        (total, stop) => total + stop.totalLoadWeight,
        0
      );
      this.masterLading.totalLoadPallets = this.consolidationStops.reduce(
        (total, stop) => total + stop.totalLoadPallets,
        0
      );
      this.masterLading.totalLoadLength = this.consolidationStops.reduce(
        (total, stop) => total + stop.totalLoadLength,
        0
      );



    } else {
      this.masterLading = {
        shipper: '',
        consignee: '',
        totalLoadPieces: 0,
        totalLoadWeight: 0,
        totalLoadPallets: 0,
      };
    }
  }

  saveConsolidation() {
    if (this.isSaving || !this.recoveryReady) return;
    let request = this.pendingConsolidationRequest;
    const recovering = !!request;
    if (!request) {
      if (!this.consolidationStops.length) { this.consolidationMessage = 'Add at least one stop.'; return; }
      if (!this.masterShipper?.contact_uuid || !this.masterCarrier?.contact_uuid || !this.masterLading?.consigneeContact?.contact_uuid ||
          this.consolidationStops.some(stop => !stop.selectedConsignee?.contact_uuid || !stop.shipperContact?.contact_uuid)) {
        this.consolidationMessage = 'Select the shipper, carrier and consignee for each stop.'; return;
      }
      const stops = this.consolidationStops.map((stop, index) => ({
        sequence: index + 1,
        shipper_contact_uuid: stop.shipperContact.contact_uuid,
        consignee_contact_uuid: stop.selectedConsignee!.contact_uuid,
        ladings: stop.ladings.map(lading => ({ lading_uuid: lading.lading_uuid }))
      }));
      request = {
        master_shipper_contact_uuid: this.masterShipper.contact_uuid,
        master_carrier_contact_uuid: this.masterCarrier.contact_uuid,
        master_consignee_contact_uuid: this.masterLading.consigneeContact.contact_uuid,
        stops
      };
      this.retainConsolidationRequest(request);
    }
    this.isSaving = true;
    this.consolidationMessage = 'Checking consolidation save…';
    this.http.post('/api/work_orders/lading_create_consolidation.php', request).subscribe({
      next: response => this.handleConsolidationReply(response, 200, recovering),
      error: error => this.handleConsolidationReply(error?.error, error?.status || 0, recovering)
    });
  }

  private retainConsolidationRequest(request: any): void {
    this.pendingConsolidationRequest = request;
    try {
      if (this.pendingStorageKey) {
        if (request) sessionStorage.setItem(this.pendingStorageKey, JSON.stringify(request));
        else sessionStorage.removeItem(this.pendingStorageKey);
      }
    } catch { /* The in-memory request remains frozen if browser storage is unavailable. */ }
  }

  private handleConsolidationReply(response: any, status: number, recovering = false): void {
    this.isSaving = false;
    const result = Array.isArray(response) && response.length === 1 ? response[0] : null;
    if (status === 200 && result?.code === '200' && result?.success === true) {
      this.retainConsolidationRequest(null);
      this.consolidationMessage = 'Consolidation saved successfully.';
      this.toastr.success('Consolidation saved successfully.');
      this.router.navigate(['/consolidations']);
      return;
    }
    const code = Number(result?.code || status);
    const owned = !!(result?.request_uuid || result?.operation_uuid || result?.lading_consolidation_uuid);
    if (result && [400,403,409,422].includes(code) && !owned && !recovering) {
      this.retainConsolidationRequest(null);
      this.consolidationMessage = (result.message || 'Check the selected consolidation details.') +
        (result.error ? ' (' + result.error + ')' : '');
      return;
    }
    if (code === 409 && (owned || recovering)) {
      this.consolidationMessage = 'This saved consolidation needs review in Roswell status. Its selections are retained.' +
        (result.error ? ' (' + result.error + ')' : '');
      return;
    }
    this.consolidationMessage = 'The save is pending or was not confirmed. Use Check save status to recover the same consolidation.';
  }

  toggleExpand(stop: Stop) {
    this.expandedElement = this.expandedElement === stop ? null : stop;
  }

  updateStopNumbers() {
    this.consolidationStops.forEach((stop, index) => {
      stop.stopNumber = index + 1;
    });
    this.stopsDataSource.data = [...this.consolidationStops];
  }

  autoSortStops() {
    if (this.consolidationStops.length <= 1) return;

    const starting_terminal = this.masterShipper.contact_uuid;
    const terminals = this.consolidationStops.map(stop => stop.consigneeContact.contact_uuid);

    const body = {
      starting_terminal: starting_terminal,
      terminals: terminals
    };

    this.http.post<any>('/api/work_orders/rank_terminals.php', body).subscribe(
      (response: any[]) => {
        // Assuming response is sorted by stop number
        response.sort((a: any, b: any) => a.stop - b.stop);

        const sortedStops: Stop[] = response.map((item: any) => {
          return this.consolidationStops.find((stop: Stop) => stop.consigneeContact.contact_uuid === item.contact_uuid) as Stop;
        }).filter((stop): stop is Stop => stop !== undefined);

        this.consolidationStops = sortedStops.map((stop, index) => ({
          ...stop,
          stopNumber: index + 1
        }));

        this.stopsDataSource.data = [...this.consolidationStops];
        this.updateMasterLading();
      },
      (error) => {
        console.error('Error ranking stops:', error);
      }
    );
  }

  moveStopUp(stop: Stop) {
    const index = this.consolidationStops.indexOf(stop);
    if (index > 0) {
      [this.consolidationStops[index - 1], this.consolidationStops[index]] = [this.consolidationStops[index], this.consolidationStops[index - 1]];
      this.updateStopNumbers();
      this.updateMasterLading();
    }
  }

  moveStopDown(stop: Stop) {
    const index = this.consolidationStops.indexOf(stop);
    if (index < this.consolidationStops.length - 1) {
      [this.consolidationStops[index], this.consolidationStops[index + 1]] = [this.consolidationStops[index + 1], this.consolidationStops[index]];
      this.updateStopNumbers();
      this.updateMasterLading();
    }
  }

  filterCarriers() {
    // Add debounce time
      of(this.carrierFilter)
      .pipe(debounceTime(300))
      .subscribe((filterValue: string) => {
        filterValue = filterValue.toLowerCase();
        this.filteredCarriers = this.carriers.filter(carrier =>
          carrier.carrier_name.toLowerCase().includes(filterValue)
        );
      });
  }

  handleCarrierChange() {
    if (this.selectedCarrier) {
      this.masterCarrier = {
        name: this.selectedCarrier.carrier_name,
        contact_uuid: this.selectedCarrier.carrier_contact_uuid,
      };
      this.updateMasterLading();
    }
  }


  fetchCarriers() {
    this.http.get<Carrier[]>('/api/work_orders/get_carriers.php').subscribe(
      (response) => {
        if (Array.isArray(response)) {
          this.carriers = response;
          this.filteredCarriers = this.carriers.slice(); // Initialize filteredCarriers
        } else {
          console.error('Unexpected carriers response format:', response);
        }
      },
      (error) => {
        console.error('Error fetching carriers:', error);
      }
    );
  }

}
